import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Stethoscope, GraduationCap, Cpu, BookOpen, Award, ChevronRight, Pause, Play, RotateCw, Sparkles, Globe } from 'lucide-react';
import './Programs3DCube.css';

// Revolving virtual hologram globe.
// Six program-family hologram tags orbit a translucent cyan sphere wrapped
// in a latitude/longitude wireframe. Below the stage, the MedCare AI
// "Evolving Essence" tagline cycles through 6 living phrases.
const FACES = [
  {
    key: 'healthcare',
    icon: Stethoscope,
    title: 'Healthcare',
    accent: '#0d9488',
    items: [
      { id: 'cna-prep',                 name: 'CMA',                    meta: '4-6 weeks · 20 clinical hrs' },
      { id: 'emt-prep',                 name: 'EMT',                    meta: '6-8 weeks · 150 clinical hrs' },
      { id: 'phlebotomy-prep',          name: 'Phlebotomy',             meta: '4-6 weeks · 120 RTI hrs' },
      { id: 'medical-aide-prep',        name: 'Medical Aide · Outpatient',      meta: '3 weeks · 100 RTI hrs' },
    ],
  },
  {
    key: 'pn',
    icon: Heart,
    title: 'Practical Nurse — 12 Phases',
    accent: '#0f766e',
    items: [
      { id: 'lpn-prep',     name: 'Phase 1 · Foundations',    meta: 'Start here · $297 / phase' },
      { id: 'lpn-phase-2',  name: 'Phase 2 · Pharmacology',   meta: 'Unlocks after Phase 1 final' },
      { id: 'lpn-phase-6',  name: 'Phase 6 · Med-Surg',       meta: '600 supervised clinical hrs' },
      { id: 'lpn-phase-12', name: 'Phase 12 · NCLEX-PN Review', meta: 'Capstone · 12 / 12' },
    ],
    footnote: 'All 12 phases unlock automatically inside your Student Portal.',
  },
  {
    key: 'doctorate',
    icon: GraduationCap,
    title: 'Doctorate · Vital Health Sciences',
    accent: '#7c3aed',
    items: [
      { id: 'doctor-vital-health-sciences', name: 'Dr. of Vital Health Sciences',    meta: '$397 collected upfront' },
      { id: 'doctor-natural-health',        name: 'Dr. of Natural Health',           meta: '$2,103 deferred under ETA-671' },
      { id: 'doctor-holistic-wellness',     name: 'Dr. of Holistic Wellness',        meta: 'Integrative · Self-paced' },
      { id: 'herbal-health-practitioner',   name: 'Herbal Health Practitioner',      meta: 'Pharmacognosy + clinical' },
      { id: 'integrative-wellness-consultant', name: 'Integrative Wellness Consultant', meta: 'Coaching + functional nutrition' },
    ],
  },
  {
    key: 'tech-workforce',
    icon: Cpu,
    title: 'Workforce & Technology',
    accent: '#0ea5e9',
    items: [
      { id: 'cybersecurity-prep',           name: 'Cyber Security',          meta: 'CompTIA Sec+ aligned · $99' },
      { id: 'medical-billing-coding-prep',  name: 'Medical Billing & Coding',        meta: 'ICD-10 / CPT / HCPCS' },
      { id: 'forklift-osha-prep',           name: 'Forklift OSHA Certification',     meta: 'OSHA 29 CFR 1910.178' },
      { id: 'homeland-security-prep',       name: 'Master Homeland Security',        meta: 'FEMA ICS-aligned' },
    ],
  },
  {
    key: 'adult-learning',
    icon: BookOpen,
    title: 'Adult Learning Foundations',
    accent: '#1e40af',
    items: [
      { id: 'ged-prep',                  name: 'GED Adult Learning',          meta: 'Math · Science · Reasoning · LA' },
      { id: 'medical-terminology-prep',  name: 'Medical Terminology',         meta: '80-hr intensive vocabulary' },
    ],
    footnote: 'Stackable foundation for every MedCare workforce track.',
  },
  {
    key: 'why-medcare',
    icon: Award,
    title: 'Why MedCare',
    accent: '#b45309',
    items: [
      { id: 'dol',          name: 'U.S. DOL Registered Apprenticeship', meta: 'Stamped, verifiable credentials' },
      { id: 'collected',    name: '$297 collected upfront',             meta: '$99 enroll + $99 mid + $99 final' },
      { id: 'deferred',     name: 'Stated tuition deferred',            meta: 'Payable only on default · ETA-671' },
      { id: 'verify',       name: 'Public Verify Certificate URL',      meta: 'Every cert auditable in 2 seconds' },
    ],
  },
];

const AI_TAGLINES = [
  'Synthesizing apprenticeship intelligence…',
  'Mapping 27 career credentials in real time…',
  'Adapting curriculum to your learning pace…',
  'Issuing tamper-proof, publicly verifiable credentials…',
  'Routing graduates to U.S. DOL-registered employers…',
  'Evolving toward a fully autonomous training ecosystem…',
];

const Programs3DCube = () => {
  const [rotating, setRotating] = useState(true);
  const [activeFace, setActiveFace] = useState(0);
  const [introDone, setIntroDone] = useState(false);
  const [tagIdx, setTagIdx] = useState(0);
  const sceneRef = useRef(null);

  // Cinematic intro — globe dollies in from far for 1.85s
  useEffect(() => {
    const t = setTimeout(() => setIntroDone(true), 1850);
    return () => clearTimeout(t);
  }, []);

  // Step the active program tag every 4s so the side panel matches the
  // currently-facing tag around the globe.
  useEffect(() => {
    if (!rotating) return undefined;
    const handle = setInterval(() => {
      setActiveFace((f) => (f + 1) % FACES.length);
    }, 4000);
    return () => clearInterval(handle);
  }, [rotating]);

  // AI evolving essence — tagline cycles every 3.2s.
  useEffect(() => {
    const handle = setInterval(() => {
      setTagIdx((i) => (i + 1) % AI_TAGLINES.length);
    }, 3200);
    return () => clearInterval(handle);
  }, []);

  const face = FACES[activeFace];
  const FaceIcon = face.icon;

  return (
    <div className="cube3d-shell" data-testid="programs-3d-cube">
      <div className="cube3d-stars" aria-hidden="true" />
      <div className="cube3d-grid"  aria-hidden="true" />
      <div className="cube3d-scanlines" aria-hidden="true" />
      <div className="cube3d-aurora" aria-hidden="true" />

      <div className="cube3d-layout">
        {/* Stage */}
        <div
          className={`cube3d-stage ${introDone ? 'is-ready' : 'is-intro'}`}
          ref={sceneRef}
          onMouseEnter={() => setRotating(false)}
          onMouseLeave={() => setRotating(true)}
        >
          {/* Hologram projection beam from the floor */}
          <div className="globe-beam" aria-hidden="true" />

          {/* Aurora halos pulsing around the globe */}
          <div className="cube3d-aura cube3d-aura-1" aria-hidden="true" />
          <div className="cube3d-aura cube3d-aura-2" aria-hidden="true" />

          <div className={`cube3d-cube ${rotating ? 'is-spinning' : ''}`} style={{ '--face-color': face.accent }}>

            {/* ============ Hologram globe core ============ */}
            <div className="globe-core" aria-hidden="true">
              {/* Latitude rings (3) */}
              <span className="globe-lat globe-lat-1" />
              <span className="globe-lat globe-lat-2" />
              <span className="globe-lat globe-lat-3" />
              <span className="globe-lat globe-lat-equator" />
              {/* Longitude meridians (6) */}
              <span className="globe-lng globe-lng-1" />
              <span className="globe-lng globe-lng-2" />
              <span className="globe-lng globe-lng-3" />
              <span className="globe-lng globe-lng-4" />
              <span className="globe-lng globe-lng-5" />
              <span className="globe-lng globe-lng-6" />
              {/* Continent-like glow specks */}
              <span className="globe-speck globe-speck-1" />
              <span className="globe-speck globe-speck-2" />
              <span className="globe-speck globe-speck-3" />
              <span className="globe-speck globe-speck-4" />
              <span className="globe-speck globe-speck-5" />
              {/* Fresnel rim & inner core glow */}
              <span className="globe-rim" />
              <span className="globe-inner-glow" />
              {/* MedCare seal floating at the equator front */}
              <div className="globe-seal">
                <img src={`${process.env.PUBLIC_URL || ''}/medcare_seal.png`} alt="" />
              </div>
            </div>

            {/* ============ Six orbital program tags ============ */}
            {FACES.map((f, idx) => {
              const FIcon = f.icon;
              const isActive = idx === activeFace;
              return (
                <div
                  key={f.key}
                  className={`cube3d-face cube3d-face-${idx} ${isActive ? 'is-front' : ''}`}
                  data-testid={`cube-face-${f.key}`}
                  style={{ '--accent': f.accent }}
                >
                  <div className="cube3d-face-inner">
                    <div className="cube3d-face-header">
                      <FIcon size={18} />
                      <span>{f.title}</span>
                    </div>
                    <ul className="cube3d-face-list">
                      {f.items.slice(0, 3).map((it) => (
                        <li key={it.id}>
                          <span className="cube3d-face-name">{it.name}</span>
                          <span className="cube3d-face-meta">{it.meta}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* AI evolving essence pill */}
          <div className="cube3d-ai-essence" data-testid="cube-ai-essence">
            <Sparkles size={14} className="cube3d-ai-spark" />
            <span className="cube3d-ai-label">MedCare AI · Evolving Essence</span>
            <span className="cube3d-ai-divider">·</span>
            <span key={tagIdx} className="cube3d-ai-tagline" data-testid="cube-ai-tagline">
              {AI_TAGLINES[tagIdx]}
            </span>
          </div>
        </div>

        {/* Side panel — describes the currently-visible tag */}
        <aside className="cube3d-panel" data-testid="cube-panel" style={{ '--panel-accent': face.accent }}>
          <div className="cube3d-panel-eyebrow">
            <Globe size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Now Orbiting · Tag {activeFace + 1} of {FACES.length} · Hologram Globe
          </div>
          <h2 className="cube3d-panel-title">
            <FaceIcon size={26} />
            {face.title}
          </h2>
          <ul className="cube3d-panel-list" data-testid="cube-panel-list">
            {face.items.map((it) => (
              <li key={it.id} className="cube3d-panel-item">
                <Link
                  to={`/online-courses/${it.id}`}
                  className="cube3d-panel-item-link"
                  data-testid={`cube-panel-item-${it.id}`}
                >
                  <div>
                    <div className="cube3d-panel-name">{it.name}</div>
                    <div className="cube3d-panel-meta">{it.meta}</div>
                  </div>
                  <ChevronRight size={16} className="cube3d-panel-arrow" />
                </Link>
              </li>
            ))}
          </ul>
          {face.footnote && <p className="cube3d-panel-footnote">{face.footnote}</p>}

          <div className="cube3d-panel-actions">
            <Link to="/register" className="cube3d-btn primary" data-testid="cube-cta-register">
              Create Account · Start Learning <ChevronRight size={16} />
            </Link>
            <Link to="/login" className="cube3d-btn ghost" data-testid="cube-cta-login">
              Student Login
            </Link>
            <Link to="/online-courses" className="cube3d-btn ghost" data-testid="cube-cta-courses">
              All programs
            </Link>
            <button
              type="button"
              onClick={() => setRotating((r) => !r)}
              className="cube3d-btn ghost"
              data-testid="cube-toggle-rotate"
              aria-label={rotating ? 'Pause rotation' : 'Resume rotation'}
            >
              {rotating ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Play</>}
            </button>
            <button
              type="button"
              onClick={() => setActiveFace((f) => (f + 1) % FACES.length)}
              className="cube3d-btn ghost"
              data-testid="cube-next-face"
            >
              <RotateCw size={14} /> Next
            </button>
          </div>
        </aside>
      </div>

      <div className="cube3d-footer-strip">
        <span>U.S. Department of Labor · Registered Apprenticeship</span>
        <span>·</span>
        <span>27 programs · 1 doorway · MedCare Nursing Institute &amp; Training Academy</span>
      </div>
    </div>
  );
};

export default Programs3DCube;
